/**
 * FRW Persona Browse — AI-powered product reordering.
 *
 * Reads the visitor's persona (vibe/artist/festival) from localStorage,
 * fetches artist↔product match data from the theme asset, and reorders
 * product grids so the most relevant products appear first.
 *
 * Zero overhead if no persona exists (early return).
 * Loaded on homepage (via portal section) and collection pages.
 */
(function() {
  'use strict';

  // ─── EARLY EXIT: no persona ───
  var raw = localStorage.getItem('frw_persona');
  if (!raw) return;

  var persona;
  try { persona = JSON.parse(raw); } catch(e) { return; }
  if (!persona) return;

  var CACHE_KEY = 'frw_match_data';
  var CACHE_ARCH = 'frw_arch_data';
  var CACHE_TTL = 3600000; // 1 hour

  // Vibe accent colors (matches portal theme)
  var VIBE_COLORS = {
    dark: { accent: '#9333ea', bg: 'rgba(147, 51, 234, 0.08)', border: 'rgba(147, 51, 234, 0.25)' },
    electric: { accent: '#E040FB', bg: 'rgba(121, 253, 156, 0.06)', border: 'rgba(121, 253, 156, 0.2)' },
    ethereal: { accent: '#c4b5fd', bg: 'rgba(196, 181, 253, 0.06)', border: 'rgba(196, 181, 253, 0.2)' },
    wild: { accent: '#CF6FFF', bg: 'rgba(255, 46, 151, 0.06)', border: 'rgba(255, 46, 151, 0.2)' },
    minimal: { accent: '#f5f7f9', bg: 'rgba(245, 247, 249, 0.04)', border: 'rgba(245, 247, 249, 0.12)' }
  };

  // ─── WELCOME BANNER (from portal redirect) ───
  function showWelcomeBanner() {
    var params = new URLSearchParams(window.location.search);
    if (!params.has('from') || params.get('from') !== 'portal') return;
    if (sessionStorage.getItem('frw_banner_shown')) return;

    sessionStorage.setItem('frw_banner_shown', '1');

    // Clean the URL (remove ?from=portal)
    var clean = window.location.pathname;
    window.history.replaceState({}, '', clean);

    var colors = VIBE_COLORS[persona.vibe] || VIBE_COLORS.electric;
    var message = persona.artist
      ? 'Curated for <strong>' + persona.artist + '</strong> fans'
      : 'Curated for your <strong>' + (persona.vibe || '') + '</strong> vibe';

    var banner = document.createElement('div');
    banner.id = 'frw-persona-banner';
    banner.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;gap:12px;padding:12px 20px;' +
      'background:' + colors.bg + ';border-bottom:1px solid ' + colors.border + ';' +
      'font-family:var(--font-body--family,sans-serif);font-size:14px;color:var(--color-foreground,#f5f7f9);">' +
      '<span style="color:' + colors.accent + ';">\u2726</span> ' +
      '<span>' + message + ' \u2014 products ranked by visual match</span>' +
      '<button onclick="this.closest(\'#frw-persona-banner\').remove()" ' +
      'style="background:none;border:none;color:var(--color-foreground,#999);cursor:pointer;font-size:18px;' +
      'padding:0 4px;line-height:1;" aria-label="Dismiss">&times;</button>' +
      '</div>';

    // Insert at the top of the main content area
    var main = document.querySelector('main') || document.querySelector('.main-content') || document.body.firstElementChild;
    if (main && main.parentNode) {
      main.parentNode.insertBefore(banner, main);
    }
  }

  // ─── FETCH & CACHE MATCH DATA ───
  function getMatchData(cb) {
    var cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        var p = JSON.parse(cached);
        if (p.ts && Date.now() - p.ts < CACHE_TTL) { cb(p.data); return; }
      } catch(e) { /* stale cache, refetch */ }
    }

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'assets/artist-matches.json')
      .then(function(r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function(data) {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: data, ts: Date.now() }));
        cb(data);
      })
      .catch(function() {
        // Match data not yet deployed — silent, no error
      });
  }

  // ─── EXTRACT HANDLE FROM PRODUCT CARD ───
  function getHandle(el) {
    var a = el.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^?#\/]+)/);
    return m ? m[1] : null;
  }

  // ─── REORDER ITEMS INSIDE A CONTAINER ───
  function reorderItems(container, itemSelector, handles) {
    var items = Array.from(container.querySelectorAll(itemSelector));
    if (items.length < 2) return 0;

    var matched = [];
    var rest = [];

    items.forEach(function(el) {
      var h = getHandle(el);
      var idx = h ? handles.indexOf(h) : -1;
      if (idx >= 0) {
        matched.push({ el: el, rank: idx });
      } else {
        rest.push(el);
      }
    });

    if (!matched.length) return 0;

    // Sort matched by rank (lower = stronger match)
    matched.sort(function(a, b) { return a.rank - b.rank; });

    // Re-append in new order: matched first, then the rest
    matched.forEach(function(m) { container.appendChild(m.el); });
    rest.forEach(function(el) { container.appendChild(el); });

    return matched.length;
  }

  // ─── MAIN ───
  function applyPersona(matchData) {
    if (!matchData) return;

    // Case-insensitive artist lookup
    var artistKey = Object.keys(matchData).find(function(k) {
      return k.toLowerCase() === persona.artist.toLowerCase();
    });

    if (!artistKey || !matchData[artistKey] || !matchData[artistKey].length) return;

    var handles = matchData[artistKey];
    var totalMatched = 0;

    // Collection pages: <ul class="product-grid"> > <li class="product-grid__item">
    document.querySelectorAll('.product-grid').forEach(function(grid) {
      totalMatched += reorderItems(grid, '.product-grid__item', handles);
    });

    // Homepage product lists: <div class="resource-list--grid"> > <div class="resource-list__item">
    document.querySelectorAll('.resource-list--grid').forEach(function(grid) {
      totalMatched += reorderItems(grid, '.resource-list__item', handles);
    });

    if (totalMatched > 0) {
      document.dispatchEvent(new CustomEvent('frw:persona-applied', {
        detail: {
          artist: persona.artist,
          vibe: persona.vibe,
          festival: persona.festival,
          matchCount: totalMatched
        }
      }));
    }
  }

  // ─── PDP MATCH BADGE ───
  function showPdpBadge(matchData) {
    if (!matchData || !persona.artist) return;
    var path = window.location.pathname;
    if (path.indexOf('/products/') !== 0) return;

    var handle = path.replace('/products/', '').split('?')[0].split('#')[0];
    if (!handle) return;

    var artistKey = Object.keys(matchData).find(function(k) {
      return k.toLowerCase() === persona.artist.toLowerCase();
    });
    if (!artistKey || !matchData[artistKey]) return;

    var rank = matchData[artistKey].indexOf(handle);
    if (rank < 0) return;

    var colors = VIBE_COLORS[persona.vibe] || VIBE_COLORS.electric;
    var strength = rank < 5 ? 'Top pick' : rank < 10 ? 'Great match' : 'Matched';

    var badge = document.createElement('div');
    badge.id = 'frw-pdp-match';
    badge.innerHTML =
      '<div style="display:inline-flex;align-items:center;gap:8px;padding:8px 14px;' +
      'background:' + colors.bg + ';border:1px solid ' + colors.border + ';border-radius:6px;' +
      'font-family:var(--font-body--family,sans-serif);font-size:13px;margin-bottom:12px;">' +
      '<span style="color:' + colors.accent + ';">\u2726</span> ' +
      '<span>' + strength + ' for <strong>' + persona.artist + '</strong> fans</span>' +
      '</div>';

    // Insert above the product title or at the top of product details
    var title = document.querySelector('h1');
    if (title && title.parentNode) {
      title.parentNode.insertBefore(badge, title);
    }
  }

  // ─── FETCH & CACHE ARCHETYPE MATCH DATA ───
  function getArchetypeData(cb) {
    var cached = sessionStorage.getItem(CACHE_ARCH);
    if (cached) {
      try {
        var p = JSON.parse(cached);
        if (p.ts && Date.now() - p.ts < CACHE_TTL) { cb(p.data); return; }
      } catch(e) {}
    }

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'assets/archetype-matches.json')
      .then(function(r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function(data) {
        sessionStorage.setItem(CACHE_ARCH, JSON.stringify({ data: data, ts: Date.now() }));
        cb(data);
      })
      .catch(function() {});
  }

  // ─── ARCHETYPE-BASED REORDERING (fallback when no artist match) ───
  function applyArchetype(archData) {
    if (!archData || !persona.archetype) return;
    var arch = archData[persona.archetype];
    if (!arch || !arch.handles || !arch.handles.length) return;

    var totalMatched = 0;
    document.querySelectorAll('.product-grid').forEach(function(grid) {
      totalMatched += reorderItems(grid, '.product-grid__item', arch.handles);
    });
    document.querySelectorAll('.resource-list--grid').forEach(function(grid) {
      totalMatched += reorderItems(grid, '.resource-list__item', arch.handles);
    });

    if (totalMatched > 0) {
      document.dispatchEvent(new CustomEvent('frw:persona-applied', {
        detail: {
          archetype: persona.archetype,
          vibe: arch.vibe || persona.vibe,
          matchCount: totalMatched,
          source: 'archetype'
        }
      }));
    }
  }

  function init() {
    showWelcomeBanner();

    // Priority: artist match > archetype match
    if (persona.artist) {
      getMatchData(function(data) {
        applyPersona(data);
        showPdpBadge(data);
      });
    } else if (persona.archetype) {
      getArchetypeData(function(data) {
        applyArchetype(data);
      });
    }
  }

  // Run after DOM is ready (slight delay for hydration of web components)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(init, 50);
    });
  } else {
    setTimeout(init, 50);
  }

  // Also respond to portal completion on the same page
  window.addEventListener('frw:persona-ready', function() {
    // Re-read persona (it was just saved)
    raw = localStorage.getItem('frw_persona');
    if (!raw) return;
    try { persona = JSON.parse(raw); } catch(e) { return; }
    if (persona && persona.artist) init();
  });
})();
